import { triada } from '@/app/helpers';
import { DidoxDocument, GetDidoxDocuments } from '@/entities/didox/types';
import { EmptyDataPlaceholder } from '@/shared/ui/empty-data-placeholder';
import { UITable } from '@/shared/ui/ui-table';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { numericFormatter } from 'react-number-format';

interface Props {
  documents?: GetDidoxDocuments;
  isFetching: boolean;
  isLoading: boolean;
  onChoose: (document: DidoxDocument) => void;
  selected: DidoxDocument[];
  setSelected: React.Dispatch<React.SetStateAction<DidoxDocument[]>>;
  isMultiSelect: boolean;
  preSelectedDocIds?: string[];
}

export const DidoxDocumentsModalTable: FC<Props> = ({
  documents,
  isFetching,
  isLoading,
  onChoose,
  selected,
  setSelected,
  isMultiSelect,
  preSelectedDocIds = [],
}) => {
  const { t } = useTranslation();
  const rowsWithFactoring =
    documents?.data
      ?.map((row, index) => ({
        index,
        hasBlocks: Array.isArray(row.factoringBlocks) && row.factoringBlocks.length > 0,
      }))
      ?.filter((r) => r.hasBlocks)
      ?.map((r) => r.index) || [];

  return (
    <UITable
      checkbox={
        isMultiSelect && {
          selected,
          setSelected,
        }
      }
      data={documents?.data || []}
      sx={{
        '& .MuiTableCell-root': {
          padding: '8px',
        },
        ...Object.fromEntries(
          rowsWithFactoring.map((i) => [
            `& tbody > tr:nth-of-type(${i + 1}) > td`,
            { paddingBottom: '60px' },
          ])
        ),
      }}
      getRowDisabled={(row) =>
        preSelectedDocIds.includes(row.doc_id) ||
        (Array.isArray(row.factoringBlocks) &&
          row.factoringBlocks.length > 0 &&
          Number(row.factoringBlocks[0].availableSum) === 0)
      }
      columns={[
        {
          label: t('document'),
          render: (row) => (
            <Stack direction='column' spacing={1}>
              <Stack direction='row' spacing={1.5} alignItems='center'>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: 'rgba(71, 191, 51, 1)',
                    borderRadius: '50%',
                    flexShrink: 0,
                    opacity:
                      Array.isArray(row.factoringBlocks) &&
                      row.factoringBlocks.length > 0 &&
                      Number(row.factoringBlocks[0].availableSum) === 0
                        ? '0.5'
                        : '1',
                  }}
                />
                <Box
                  sx={{
                    opacity:
                      Array.isArray(row.factoringBlocks) &&
                      row.factoringBlocks.length > 0 &&
                      Number(row.factoringBlocks[0].availableSum) === 0
                        ? '0.5'
                        : '1',
                  }}
                >
                  <Typography fontSize={14}>{row.name}</Typography>
                  <Typography fontSize={14}>{t('from_value', { date: row.created })}</Typography>
                </Box>
              </Stack>

              {Array.isArray(row.factoringBlocks) && row.factoringBlocks.length > 0 && (
                <Alert
                  severity={Number(row.factoringBlocks[0].availableSum) !== 0 ? 'error' : 'warning'}
                  sx={{
                    backgroundColor:
                      Number(row.factoringBlocks[0].availableSum) !== 0 ? '#FFAB001A' : '#EC53111A',
                    color: '#0B1370',
                    fontSize: 14,
                    fontWeight: 500,
                    mt: 10,
                    px: 1,
                    py: 0,
                    position: 'absolute',
                    top: '50%',
                    '& .MuiAlert-icon': {
                      color:
                        Number(row.factoringBlocks[0].availableSum) !== 0 ? '#FFAB00' : '#EC5311',
                    },
                  }}
                >
                  {Number(row.factoringBlocks[0].availableSum) !== 0
                    ? t('factoring_partially_available_alert', {
                        numbers: row.factoringBlocks.map((b) => `№${b.factoringId}`).join(', '),
                        totalSum: triada(
                          row.factoringBlocks.reduce(
                            (acc, curr) => acc + Number(curr.blockedSum),
                            0
                          )
                        ),
                        availableSum: triada(Number(row.factoringBlocks[0].availableSum)),
                      })
                    : t('factoring_blocked_alert', {
                        numbers: row.factoringBlocks.map((b) => `№${b.factoringId}`).join(', '),
                      })}
                </Alert>
              )}
            </Stack>
          ),
        },
        {
          label: t('updated'),
          render: (row) => (
            <Stack
              direction='column'
              spacing={1.5}
              sx={{
                opacity:
                  Array.isArray(row.factoringBlocks) &&
                  row.factoringBlocks.length > 0 &&
                  Number(row.factoringBlocks[0].availableSum) === 0
                    ? '0.5'
                    : '1',
              }}
            >
              <Box>
                <Typography fontSize={14}>{row.updated}</Typography>
                <Typography fontSize={14}>{t(`doctypes.${row.doctype}`)}</Typography>
              </Box>
            </Stack>
          ),
        },
        {
          label: t('contragent'),
          render: (row) => (
            <Stack
              direction='column'
              spacing={1.5}
              sx={{
                opacity:
                  Array.isArray(row.factoringBlocks) &&
                  row.factoringBlocks.length > 0 &&
                  Number(row.factoringBlocks[0].availableSum) === 0
                    ? '0.5'
                    : '1',
              }}
            >
              <Box>
                <Typography fontSize={14}>{row.partnerCompany}</Typography>
                <Typography fontSize={14}>{row.partnerTin}</Typography>
              </Box>
            </Stack>
          ),
        },
        {
          label: t('contract'),
          render: (row) => (
            <Stack
              direction='column'
              spacing={1.5}
              sx={{
                opacity:
                  Array.isArray(row.factoringBlocks) &&
                  row.factoringBlocks.length > 0 &&
                  Number(row.factoringBlocks[0].availableSum) === 0
                    ? '0.5'
                    : '1',
              }}
            >
              <Box>
                <Typography fontSize={14}>{row.contract_number}</Typography>
                <Typography fontSize={14}>{row.contract_date}</Typography>
              </Box>
            </Stack>
          ),
        },
        {
          label: t('sum'),
          render: (row) =>
            row.total_delivery_sum ? (
              <Typography
                sx={{
                  opacity:
                    Array.isArray(row.factoringBlocks) &&
                    row.factoringBlocks.length > 0 &&
                    Number(row.factoringBlocks[0].availableSum) === 0
                      ? '0.5'
                      : '1',
                }}
              >
                {numericFormatter(String(row.total_delivery_sum), {
                  thousandSeparator: ' ',
                })}
              </Typography>
            ) : (
              <Typography
                sx={{
                  opacity:
                    Array.isArray(row.factoringBlocks) &&
                    row.factoringBlocks.length > 0 &&
                    Number(row.factoringBlocks[0].availableSum) === 0
                      ? '0.5'
                      : '1',
                }}
              >
                0
              </Typography>
            ),
          align: 'right',
          headerAlign: 'right',
        },
        {
          label: t('vat'),
          render: (row) =>
            row.total_vat_sum ? (
              <Typography
                sx={{
                  opacity:
                    Array.isArray(row.factoringBlocks) &&
                    row.factoringBlocks.length > 0 &&
                    Number(row.factoringBlocks[0].availableSum) === 0
                      ? '0.5'
                      : '1',
                }}
              >
                {numericFormatter(String(row.total_vat_sum), {
                  thousandSeparator: ' ',
                })}
              </Typography>
            ) : (
              <Typography
                sx={{
                  opacity:
                    Array.isArray(row.factoringBlocks) &&
                    row.factoringBlocks.length > 0 &&
                    Number(row.factoringBlocks[0].availableSum) === 0
                      ? '0.5'
                      : '1',
                }}
              >
                0
              </Typography>
            ),
          align: 'right',
          headerAlign: 'right',
        },
        {
          label: t('sum_with_vat'),
          render: (row) =>
            row.total_delivery_sum_with_vat ? (
              <Typography
                sx={{
                  opacity:
                    Array.isArray(row.factoringBlocks) &&
                    row.factoringBlocks.length > 0 &&
                    Number(row.factoringBlocks[0].availableSum) === 0
                      ? '0.5'
                      : '1',
                }}
              >
                {numericFormatter(String(row.total_delivery_sum_with_vat), {
                  thousandSeparator: ' ',
                })}
              </Typography>
            ) : (
              <Typography
                sx={{
                  opacity:
                    Array.isArray(row.factoringBlocks) &&
                    row.factoringBlocks.length > 0 &&
                    Number(row.factoringBlocks[0].availableSum) === 0
                      ? '0.5'
                      : '1',
                }}
              >
                0
              </Typography>
            ),
          align: 'right',
          headerAlign: 'right',
        },
      ]}
      EmptyPlaceholder={
        <EmptyDataPlaceholder isEmpty={!documents?.data.length} title={t('no_documents')} />
      }
      isLoading={isFetching || isLoading}
      hoveredElementOnClick={!isMultiSelect ? onChoose : undefined}
      hoveredBtnText={!isMultiSelect ? t('choose_document') : undefined}
    />
  );
};
